import {
  imOpToObject
} from '@bumblehead/im'

import {
  gnutilStringify as utilStringify
} from './gnutil.js'

import {
  gnenumKEY,
  gnenumPARTKEYFULL as PARTKEYFULL
} from './gnenum.js'

const gnerrLog = (cfg, gr, nd, otherDetails) => {
  cfg.logger.error({
    gr: gr && imOpToObject(gr),
    nd: nd && imOpToObject(nd),
    ...otherDetails
  })
}

const gnerr = (cfg, ...args) => {
  // lastarg typically a string or an existing error
  const lastarg = args.slice(-1)[0]
  const errinst = lastarg instanceof Error
    ? lastarg : new Error(lastarg)

  return errinst
}

const gnerr_cfgenvinvalidorigin = () => new Error(
  `[!!!] envcfg must define an "origin"`)

const gnerr_cfginvalidsess = sess => new Error(
  "[!!!] cfg must define a valid session with locale and requrl properties,"
    + ` current session: ${utilStringify(sess)}`)

const gnerr_rootnodenotfound = gr => (
  gnerr(`root node, key '/', not found: ${utilStringify(imOpToObject(gr))}`))

const gnerr_cfgreqdfnnotfound = (cfg, fnname) => (
  gnerr(cfg, `required function not defined, "${fnname}"`))

const gnerr_cfgreqdfnnotfoundpagespecfromisopath = cfg => (
  gnerr_cfgreqdfnnotfound(cfg, 'pagespecfromisopath'))

// was useful for browser troubleshooting. commented-out but possibly removable
// const gnerrnode = (graph, node, ...args) => {
//   if (typeof window === 'object') {
//     window.errgraph = graph
//     window.errnode = node
//   }
//   console.error('errgraph: ', graph.toJS())
//   console.error('errnode: ', node.toJS())
//
//   return gnerr(gnlog_getnodekey(node), ...args)
// }

const gnerrinst = (graph, node, msg) => (
  Object.assign(new Error((
    node ? node.get(gnenumKEY) + '\n' + msg : msg
  )), {
    graph: graph && imOpToObject(graph),
    node: node && imOpToObject(node)
  }))

// ex refspec, { spec: user }
// ex spectypes, { user: { ... } }
const gnerrnode_childspecnotfound = (gr, nd, refspec, spectypes) => (
  gnerrinst(gr, nd, [
    `childspec not found in spectypes: ${utilStringify(refspec)}`,
    `spectypes: ${utilStringify(spectypes)}`
  ].join('\n')))

const gnerrNodeNotFound = (gr, nd, key) => (
  gnerrinst(gr, nd, `node not found: "${key}"`))

const gnerr_nodespecchildinvalid = (gr, nd, childspec) => (
  gnerrinst(gr, nd, (
    'invalid childspec: (must be an object w/ name): ' +
      utilStringify(childspec))))

const gnerr_nodespecobjinvalid = (gr, nd, specobj, obj) => (
  gnerrinst(gr, nd, (
    `invalid specobj (must be an object): “${utilStringify(obj)}”, ` +
      + `associated with spec “${utilStringify(specobj)}”`
  )))

const gnerrEdgeNodeKeyNotFound = (gr, key) => (
  gnerrinst(gr, null, `edge node not found in graph: ${key}`))

const gnerrnode_edgenodenotfound = (gr, nd, key, edgespec) => {
  const msg = [
    `edgenode not found, key “${key}”, `,
    `verify fkey or path \n“${utilStringify(edgespec)}”`
  ].join('')

  return gnerrinst(gr, nd, msg)
}

const gnerrnode_keynodenotfound = (gr, nd, nodekey) => {
  const msg = `node not found, “${nodekey}”`

  return gnerrinst(gr, nd, msg)
}

const gnerr_nodechildnameinvalid = (gr, nd, ndname) => (
  gnerrinst(gr, nd, `invalid node name: "${ndname}"`))

const gnerr_nodespecnameinvalid = (gr, nd, ndspec) => (
  gnerrinst(gr, nd, (
    'node must have an unique name property: ' +
      `“${utilStringify(ndspec)}”`)))

const gnerrnode_foreignnamespacenotfound = (gr, nd, nsopts) => (
  gnerrinst(gr, nd, `foreign namespace not found, ${utilStringify(nsopts)}`))

const gnerrnode_nspropresolvedundefined = (gr, nd, ns, sig) => (
  gnerrinst(gr, nd, (
    'Query `:sig` resolved as `undefined` from `:nsprops`.')
    .replace(/:sig/, sig)
    .replace(/:nsprops/, utilStringify(ns, true))))

const gnerrNodeNSPropMustResolvePrimitive = (gr, nd, ns, sig) => (
  gnerrinst(gr, nd, (
    'Query `:sig` must resolve a number or string from `:nsprops`.')
    .replace(/:sig/, sig)
    .replace(/:nsprops/, utilStringify(ns, true))))

const gnerrNodeNSBaseMustResolveToArr = (gr, nd, base) => (
  gnerrinst(gr, nd, (
    `node ns "base" must resolve tn array, but resolves to \`:base\` instead`
      .replace(/:base/, utilStringify(base)))))

const gnerr_nodenotfound = (gr, ndkey) => (
  gnerrinst(gr, null, `node not found: ${ndkey}`))

const gnerr_nodechildnotfoundindex = (gr, nd, i) => (
  gnerrinst(gr, nd, `child key not found, index "${i}"`))

const gnerr_nodechildnotfoundvnode = (gr, nd, ndname) => (
  gnerrinst(gr, nd, (
    `childkey not found, “${ndname}” ${utilStringify(nd.get('childkey'))}`)))

const gnerr_nodepartkeyfullinvalid = (gr, nd, value) => (
  gnerrinst(gr, nd, `invalid "${PARTKEYFULL}" value: "${value}".` +
    ' should resolve to a "string" or "number" type value'))

const gnerr_nodesubjvalueinvalid = (gr, nd, value) => (
  gnerrinst(gr, nd, `invalid "subj" value: "${value}".` +
    ' if value an `undefined` value is intended, use `null` or `""` instead'))

const gnerr_nodepropselecttypeinvalid = (cfg, gr, nd, key) => (
  gnerrinst(gr, nd, `invalid selecttype: ${key}`))

const gnerr_nodeonsubjmustboolean = (gr, nd, val) => (
  gnerrinst(gr, nd, `onsubj function must return a boolean, returned "${val}"`))

const gnerrNodexNotFound = (gr, nd) => (
  gnerrinst(gr, nd, `page not found “${nd.get('node')}”, ${nd.get('name')}`))

const gnerrNodeChainMustReturnGraph = (gr, nd, spec, returned) => (
  gnerrinst(gr, nd, `chain must return graph; `
    + utilStringify(spec.chain) + `;\nreturned “${returned}”`))

const gnerr_nodeevhandlermustbelist = (gr, nd, event, eventhandler) => (
  gnerrinst(gr, nd, `event handler must be defined as list, “${event}”: `
    + utilStringify(eventhandler)))

const gnerr_isopatterninvalid = isopathtpl => new Error(
  `[!!!] invalid iso pattern: pattern must be object. path: ${isopathtpl}`)

const gnerr_isopathinvalid = isopathtpl => new Error(
  `[!!!] invalid iso path: must use baseLangLocale naming. path: ${isopathtpl}`)

const gnerrPathOnRootInvalid = pathonroot => new Error(
  `[!!!] path invalid: "${pathonroot}".`
    + ` path must be a string that starts with "$PATHROOT"`)

const gnerrPathTypeNInvalid = (pathtype, pathval) => new Error(
  `[!!!] pathtype \`${pathtype}\` invalid: "${pathval}".`)

// see `pagespecfromisopath`
// routes are defined in the manifest, for example,
// ```json
// {
//   ...manifest,
//   "routes": [ "/", "/about/", "/blog/" ]
// }
// ```
const gnerr_pathdefmanifestroutesinvalid = manifest => new Error(
  `[!!!] invalid manifest: "${manifest}", routes array required.`)

const gnerrNodexInvalid = nodex => new Error(`[!!!] invalid x: ${nodex}`)

const gnerr_cfgprefixdirinvalid = prefixdir => new Error(
  `[!!!] invalid prefixdir "${prefixdir}"; `
    + 'prefixdir must be string with begin-slash and end-slash chars.'
    + ' ex, "/myprefixdir/"')

const gnerr_pathdefitemnotfound = (cfg, pathitem, pathdefarr) => (
  gnerr(cfg, (
    `pathdef not found for pathitem "${pathitem}", `
      + utilStringify(pathdefarr))))

const gnerr_pathdeffullpathnotfound = (cfg, fullurl, pathdefarr) => (
  gnerr(cfg, (
    `pathdef not found for fullurl "${fullurl}", `
      + utilStringify(pathdefarr))))

const gnerrClientNSForeignPathNotFound = (gr, nd, nsstr, fpath) => (
  gnerrinst(gr, nd, `foreign path node not found "${fpath}", (${nsstr})`))

const gnerr_pathdefuripatternnotfound = (rulepath, patternname) => new Error(
  `[!!!] path uri "${rulepath}" empty or name not found: "${patternname}"`)

const gnerrCfgProbeLocaleEnvRequiredWeb = cfg => (
  gnerr(cfg, 'The app must define `envcfg.probelocaleenv`, because its manifest'
    + ' suports multiple locales. Definitions for this function will likely'
    + ' use `window.navigator`. The dependant app must provide the exact'
    + ' function used; requirements and needs for each app may differ.'))

export {
  gnerr,
  gnerrNodexInvalid,
  gnerrNodexNotFound,

  gnerr_nodenotfound,
  gnerr_nodeonsubjmustboolean,
  gnerr_nodespecnameinvalid,
  gnerr_nodespecobjinvalid,
  gnerr_nodespecchildinvalid,

  gnerr_nodechildnameinvalid,
  gnerr_nodechildnotfoundindex,
  gnerr_nodechildnotfoundvnode,

  gnerr_nodepropselecttypeinvalid,
  gnerrNodeChainMustReturnGraph,
  gnerr_nodeevhandlermustbelist,
  gnerr_rootnodenotfound,

  gnerr_cfgreqdfnnotfoundpagespecfromisopath,
  gnerr_cfgprefixdirinvalid,
  gnerrCfgProbeLocaleEnvRequiredWeb,

  gnerrnode_childspecnotfound,

  gnerrnode_edgenodenotfound,
  gnerrnode_keynodenotfound,
  gnerrnode_foreignnamespacenotfound,

  gnerrnode_nspropresolvedundefined,
  gnerrNodeNSPropMustResolvePrimitive,
  gnerrNodeNSBaseMustResolveToArr,

  // newer errors added after esm refactor
  gnerr_isopatterninvalid,
  gnerr_isopathinvalid,

  gnerr_nodepartkeyfullinvalid,
  gnerr_nodesubjvalueinvalid,

  gnerr_cfgenvinvalidorigin,
  gnerr_cfginvalidsess,

  gnerr_pathdefuripatternnotfound,
  gnerr_pathdefmanifestroutesinvalid,
  gnerr_pathdefitemnotfound,
  gnerr_pathdeffullpathnotfound,

  gnerrLog,
  gnerrNodeNotFound,
  gnerrEdgeNodeKeyNotFound,
  gnerrClientNSForeignPathNotFound,
  gnerrPathTypeNInvalid,
  gnerrPathOnRootInvalid
}
